/**
 * <scroll-story-carousel> — product gallery for the Scroll Story collection
 * blocks: a vertical rail of square thumbnails selecting a large square main
 * image (inspired by ui-layouts' VerticalthumbsSlider).
 *
 * Markup is server-rendered by snippets/scroll-story-product-carousel.liquid:
 * .ssc__thumb buttons (the rail) and matching .ssc__slide links (the main
 * image, one per product). This component only manages state — WHICH index is
 * active — by toggling `.is-active`; all motion lives in CSS.
 *
 * Interaction: the image changes ONLY when the user clicks a thumbnail (no
 * autoplay). The active main image links to its product. Vanilla Web Component,
 * no dependencies.
 */
class ScrollStoryCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.ssc__slide'));
    this.thumbs = Array.from(this.querySelectorAll('.ssc__thumb'));
    this.dots = Array.from(this.querySelectorAll('.ssc__dot'));
    this.count = this.slides.length;
    if (this.count <= 1) return;

    // Honour the server-marked default (the deck can start on a product other
    // than the first — see the snippet's `move_first_last`).
    const activeThumb = this.querySelector('.ssc__thumb.is-active');
    this.index = activeThumb ? parseInt(activeThumb.dataset.index, 10) || 0 : 0;

    this.addEventListener('click', (e) => {
      const arrow = e.target.closest('.ssc__arrow');
      if (arrow) {
        e.preventDefault();
        this.select(this.index + (parseInt(arrow.dataset.dir, 10) || 1));
        return;
      }
      const picker = e.target.closest('.ssc__thumb, .ssc__dot');
      if (!picker) return;
      e.preventDefault();
      this.select(parseInt(picker.dataset.index, 10) || 0);
    });

    // Swipe on the main image (touch devices): a decisively horizontal drag
    // navigates; anything vertical-ish is left to the browser so the scroll
    // story keeps scrolling. Listeners are passive — we never block scroll.
    const main = this.querySelector('.ssc__main');
    if (main) {
      main.addEventListener('touchstart', (e) => {
        const t = e.changedTouches[0];
        this._swipeX = t.clientX;
        this._swipeY = t.clientY;
      }, { passive: true });
      main.addEventListener('touchend', (e) => {
        if (this._swipeX == null) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - this._swipeX;
        const dy = t.clientY - this._swipeY;
        this._swipeX = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        this.select(this.index + (dx < 0 ? 1 : -1));
        // the tail of a swipe must not also follow the slide's product link
        const squelch = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
        main.addEventListener('click', squelch, { capture: true, once: true });
        setTimeout(() => main.removeEventListener('click', squelch, { capture: true }), 300);
      }, { passive: true });
    }

    this.render();
  }

  select(i) {
    this.index = ((i % this.count) + this.count) % this.count;
    this.render();
  }

  render() {
    this.slides.forEach((el, i) => {
      const active = i === this.index;
      el.classList.toggle('is-active', active);
      el.tabIndex = active ? 0 : -1;
    });
    this.thumbs.forEach((el, i) => {
      const active = i === this.index;
      el.classList.toggle('is-active', active);
      if (active) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    });
    this.dots.forEach((el, i) => {
      const active = i === this.index;
      el.classList.toggle('is-active', active);
      if (active) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('scroll-story-carousel')) {
  customElements.define('scroll-story-carousel', ScrollStoryCarousel);
}
